import { useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  hue: number;
  pulse: number;
  pulseSpeed: number;
}

interface FloatingShape {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  rotationSpeed: number;
  size: number;
  opacity: number;
  type: 'triangle' | 'hexagon' | 'ring' | 'diamond';
  hue: number;
  depth: number;
}

interface GlowOrb {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  size: number;
  hue: number;
  trail: { x: number; y: number; opacity: number }[];
}

interface Ripple {
  x: number;
  y: number;
  radius: number;
  maxRadius: number;
  opacity: number;
}

export function ParticleBackground() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const particlesRef = useRef<Particle[]>([]);
  const shapesRef = useRef<FloatingShape[]>([]);
  const orbsRef = useRef<GlowOrb[]>([]);
  const ripplesRef = useRef<Ripple[]>([]);
  const animationRef = useRef<number>();
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const timeRef = useRef(0);
  const lastClickRef = useRef(0);

  const createParticles = useCallback((width: number, height: number) => {
    const count = Math.floor((width * height) / 12000);
    particlesRef.current = [];

    for (let i = 0; i < count; i++) {
      particlesRef.current.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        size: Math.random() * 2.5 + 0.5,
        opacity: Math.random() * 0.6 + 0.2,
        hue: 230 + Math.random() * 60, // Blue to purple range
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.02 + Math.random() * 0.03,
      });
    }
  }, []);

  const createShapes = useCallback((width: number, height: number) => {
    const count = Math.floor((width * height) / 80000);
    shapesRef.current = [];
    const types: FloatingShape['type'][] = ['triangle', 'hexagon', 'ring', 'diamond'];

    for (let i = 0; i < count; i++) {
      shapesRef.current.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.01,
        size: 20 + Math.random() * 40,
        opacity: 0.05 + Math.random() * 0.1,
        type: types[Math.floor(Math.random() * types.length)],
        hue: 230 + Math.random() * 80,
        depth: 0.3 + Math.random() * 0.7,
      });
    }
  }, []);

  const createOrbs = useCallback((width: number, height: number) => {
    orbsRef.current = [];
    const orbCount = 3;

    for (let i = 0; i < orbCount; i++) {
      orbsRef.current.push({
        x: Math.random() * width,
        y: Math.random() * height,
        targetX: Math.random() * width,
        targetY: Math.random() * height,
        size: 60 + Math.random() * 100,
        hue: 230 + Math.random() * 80,
        trail: [],
      });
    }
  }, []);

  const drawShape = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      shape: FloatingShape,
      color: string
    ) => {
      ctx.save();
      ctx.translate(shape.x, shape.y);
      ctx.rotate(shape.rotation);

      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();

      const s = shape.size / 2;

      switch (shape.type) {
        case 'triangle':
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.866, s * 0.5);
          ctx.lineTo(-s * 0.866, s * 0.5);
          ctx.closePath();
          break;
        case 'hexagon':
          for (let i = 0; i < 6; i++) {
            const angle = (i * Math.PI) / 3;
            const x = Math.cos(angle) * s;
            const y = Math.sin(angle) * s;
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          }
          ctx.closePath();
          break;
        case 'ring':
          ctx.arc(0, 0, s, 0, Math.PI * 2);
          break;
        case 'diamond':
          ctx.moveTo(0, -s);
          ctx.lineTo(s * 0.6, 0);
          ctx.lineTo(0, s);
          ctx.lineTo(-s * 0.6, 0);
          ctx.closePath();
          break;
      }

      ctx.stroke();
      ctx.restore();
    },
    []
  );

  const drawAurora = useCallback(
    (ctx: CanvasRenderingContext2D, width: number, height: number, time: number) => {
      const gradient = ctx.createLinearGradient(0, height * 0.3, width, height * 0.7);

      // Animated aurora colors
      const hue1 = 230 + Math.sin(time * 0.0005) * 30;
      const hue2 = 280 + Math.cos(time * 0.0003) * 40;

      gradient.addColorStop(0, `hsla(${hue1}, 80%, 50%, 0)`);
      gradient.addColorStop(0.3, `hsla(${hue1}, 80%, 50%, 0.03)`);
      gradient.addColorStop(0.5, `hsla(${hue2}, 70%, 60%, 0.05)`);
      gradient.addColorStop(0.7, `hsla(${hue1}, 80%, 50%, 0.03)`);
      gradient.addColorStop(1, `hsla(${hue2}, 80%, 50%, 0)`);

      ctx.fillStyle = gradient;

      // Draw multiple wave layers
      for (let layer = 0; layer < 3; layer++) {
        ctx.beginPath();
        ctx.moveTo(0, height);

        for (let x = 0; x <= width; x += 5) {
          const wave1 = Math.sin(x * 0.003 + time * 0.001 + layer) * 50;
          const wave2 = Math.sin(x * 0.005 - time * 0.0008 + layer * 2) * 30;
          const wave3 = Math.sin(x * 0.001 + time * 0.0005) * 80;
          const y = height * 0.4 + wave1 + wave2 + wave3 + layer * 40;
          ctx.lineTo(x, y);
        }

        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.globalAlpha = 0.3 - layer * 0.08;
        ctx.fill();
      }

      ctx.globalAlpha = 1;
    },
    []
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.scale(dpr, dpr);
    };

    const draw = (timestamp: number) => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      timeRef.current = timestamp;

      ctx.clearRect(0, 0, width, height);

      const isDark = document.documentElement.classList.contains('dark');

      // Draw aurora/wave effect
      drawAurora(ctx, width, height, timestamp);

      // Draw glow orbs with trails
      orbsRef.current.forEach((orb) => {
        // Move toward target
        const dx = orb.targetX - orb.x;
        const dy = orb.targetY - orb.y;
        orb.x += dx * 0.003;
        orb.y += dy * 0.003;

        // Update target occasionally
        if (Math.abs(dx) < 50 && Math.abs(dy) < 50) {
          orb.targetX = Math.random() * width;
          orb.targetY = Math.random() * height;
        }

        // Add to trail
        orb.trail.unshift({ x: orb.x, y: orb.y, opacity: 0.3 });
        if (orb.trail.length > 20) orb.trail.pop();

        // Draw trail
        orb.trail.forEach((point, i) => {
          const trailSize = orb.size * (1 - i / orb.trail.length) * 0.5;
          const gradient = ctx.createRadialGradient(
            point.x,
            point.y,
            0,
            point.x,
            point.y,
            trailSize
          );
          gradient.addColorStop(0, `hsla(${orb.hue}, 80%, 60%, ${point.opacity * (1 - i / orb.trail.length) * 0.3})`);
          gradient.addColorStop(1, `hsla(${orb.hue}, 80%, 60%, 0)`);

          ctx.fillStyle = gradient;
          ctx.fillRect(point.x - trailSize, point.y - trailSize, trailSize * 2, trailSize * 2);
        });

        // Draw main orb
        const orbGradient = ctx.createRadialGradient(orb.x, orb.y, 0, orb.x, orb.y, orb.size);
        orbGradient.addColorStop(0, `hsla(${orb.hue}, 80%, 70%, 0.15)`);
        orbGradient.addColorStop(0.5, `hsla(${orb.hue}, 70%, 60%, 0.05)`);
        orbGradient.addColorStop(1, `hsla(${orb.hue}, 80%, 50%, 0)`);

        ctx.fillStyle = orbGradient;
        ctx.fillRect(orb.x - orb.size, orb.y - orb.size, orb.size * 2, orb.size * 2);
      });

      // Draw floating shapes (sorted by depth for parallax)
      const sortedShapes = [...shapesRef.current].sort((a, b) => a.depth - b.depth);

      sortedShapes.forEach((shape) => {
        // Parallax effect based on mouse
        const parallaxX = (mouseRef.current.x - width / 2) * 0.02 * shape.depth;
        const parallaxY = (mouseRef.current.y - height / 2) * 0.02 * shape.depth;

        shape.x += shape.vx;
        shape.y += shape.vy;
        shape.rotation += shape.rotationSpeed;

        // Wrap around
        if (shape.x < -shape.size) shape.x = width + shape.size;
        if (shape.x > width + shape.size) shape.x = -shape.size;
        if (shape.y < -shape.size) shape.y = height + shape.size;
        if (shape.y > height + shape.size) shape.y = -shape.size;

        const displayX = shape.x + parallaxX;
        const displayY = shape.y + parallaxY;

        const tempShape = { ...shape, x: displayX, y: displayY };
        const color = isDark
          ? `hsla(${shape.hue}, 60%, 70%, ${shape.opacity})`
          : `hsla(${shape.hue}, 70%, 50%, ${shape.opacity})`;

        drawShape(ctx, tempShape, color);
      });

      // Draw particles with enhanced effects
      const baseHue = isDark ? 230 : 240;

      particlesRef.current.forEach((particle, i) => {
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.pulse += particle.pulseSpeed;

        // Mouse interaction
        const dx = mouseRef.current.x - particle.x;
        const dy = mouseRef.current.y - particle.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 200) {
          const force = (200 - dist) / 200;
          particle.vx += (dx / dist) * force * 0.02;
          particle.vy += (dy / dist) * force * 0.02;
        }

        // Dampen and limit velocity
        particle.vx *= 0.98;
        particle.vy *= 0.98;
        particle.vx = Math.max(-2, Math.min(2, particle.vx));
        particle.vy = Math.max(-2, Math.min(2, particle.vy));

        // Wrap around
        if (particle.x < 0) particle.x = width;
        if (particle.x > width) particle.x = 0;
        if (particle.y < 0) particle.y = height;
        if (particle.y > height) particle.y = 0;

        // Pulsing size and opacity
        const pulseFactor = 0.5 + Math.sin(particle.pulse) * 0.5;
        const displaySize = particle.size * (0.8 + pulseFactor * 0.4);
        const displayOpacity = particle.opacity * (0.6 + pulseFactor * 0.4);

        // Draw particle with glow
        const particleColor = `hsla(${particle.hue}, 70%, 70%, ${displayOpacity})`;

        // Glow effect
        const glow = ctx.createRadialGradient(
          particle.x,
          particle.y,
          0,
          particle.x,
          particle.y,
          displaySize * 3
        );
        glow.addColorStop(0, `hsla(${particle.hue}, 80%, 60%, ${displayOpacity * 0.3})`);
        glow.addColorStop(1, `hsla(${particle.hue}, 80%, 60%, 0)`);

        ctx.fillStyle = glow;
        ctx.fillRect(
          particle.x - displaySize * 3,
          particle.y - displaySize * 3,
          displaySize * 6,
          displaySize * 6
        );

        // Main particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, displaySize, 0, Math.PI * 2);
        ctx.fillStyle = particleColor;
        ctx.fill();

        // Draw connections to nearby particles
        for (let j = i + 1; j < particlesRef.current.length; j++) {
          const other = particlesRef.current[j];
          const lineDist = Math.sqrt(
            Math.pow(particle.x - other.x, 2) + Math.pow(particle.y - other.y, 2)
          );

          if (lineDist < 150) {
            const lineOpacity = 0.2 * (1 - lineDist / 150);
            const gradient = ctx.createLinearGradient(
              particle.x,
              particle.y,
              other.x,
              other.y
            );
            gradient.addColorStop(0, `hsla(${particle.hue}, 60%, 60%, ${lineOpacity})`);
            gradient.addColorStop(1, `hsla(${other.hue}, 60%, 60%, ${lineOpacity})`);

            ctx.beginPath();
            ctx.moveTo(particle.x, particle.y);
            ctx.lineTo(other.x, other.y);
            ctx.strokeStyle = gradient;
            ctx.lineWidth = 0.8;
            ctx.stroke();
          }
        }
      });

      // Draw ripples
      ripplesRef.current = ripplesRef.current.filter((ripple) => {
        ripple.radius += 4;
        ripple.opacity -= 0.015;

        if (ripple.opacity <= 0) return false;

        ctx.beginPath();
        ctx.arc(ripple.x, ripple.y, ripple.radius, 0, Math.PI * 2);
        ctx.strokeStyle = `hsla(250, 80%, 70%, ${ripple.opacity})`;
        ctx.lineWidth = 2;
        ctx.stroke();

        return true;
      });

      animationRef.current = requestAnimationFrame(draw);
    };

    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current = { x: e.clientX, y: e.clientY };
    };

    const handleClick = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastClickRef.current < 100) return;
      lastClickRef.current = now;

      // Add ripple effect
      ripplesRef.current.push({
        x: e.clientX,
        y: e.clientY,
        radius: 0,
        maxRadius: 200,
        opacity: 0.5,
      });

      // Burst particles outward
      particlesRef.current.forEach((particle) => {
        const dx = particle.x - e.clientX;
        const dy = particle.y - e.clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < 300) {
          const force = (300 - dist) / 300;
          particle.vx += (dx / dist) * force * 2;
          particle.vy += (dy / dist) * force * 2;
        }
      });
    };

    resize();
    createParticles(window.innerWidth, window.innerHeight);
    createShapes(window.innerWidth, window.innerHeight);
    createOrbs(window.innerWidth, window.innerHeight);
    animationRef.current = requestAnimationFrame(draw);

    window.addEventListener('resize', () => {
      resize();
      createParticles(window.innerWidth, window.innerHeight);
      createShapes(window.innerWidth, window.innerHeight);
      createOrbs(window.innerWidth, window.innerHeight);
    });
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('click', handleClick);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('click', handleClick);
    };
  }, [createParticles, createShapes, createOrbs, drawShape, drawAurora]);

  return (
    <motion.canvas
      ref={canvasRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="fixed inset-0 -z-10 pointer-events-none"
    />
  );
}
